// Sound effects + haptics. Both are user-toggleable (persisted) and degrade
// gracefully (autoplay/vibration unsupported → silent no-op).

export type SoundName =
  | 'zap'
  | 'hit'
  | 'tap'
  | 'win'
  | 'chime' // your turn begins
  | 'death' // a runner flatlines
  | 'shield' // a spike is blocked
  | 'tick' // a planted virus ticks
  | 'boom'; // EMP detonates
type MusicMode = 'ambient' | 'duel' | null;

const SOUND_KEY = 'neonfall.sound';
const MUSIC_KEY = 'neonfall.music';
const HAPTICS_KEY = 'neonfall.haptics';

let soundEnabled = localStorage.getItem(SOUND_KEY) !== 'off';
let musicEnabled = localStorage.getItem(MUSIC_KEY) !== 'off';
let hapticsEnabled = localStorage.getItem(HAPTICS_KEY) !== 'off';
let musicMode: MusicMode = null;

// Chrome blocks navigator.vibrate() with a console Intervention until the user
// has tapped/clicked the page at least once. Track that so we can skip the
// call (rather than let the browser log a warning) for early, non-gesture
// haptics like an effect firing on mount.
let hasUserGesture = false;
// Pause music while the tab is backgrounded (switched away / minimised) and
// resume it when the player comes back — no point looping a soundtrack nobody
// can hear, and it stops music bleeding over other tabs' audio.
let pageHidden = typeof document !== 'undefined' && document.hidden;
if (typeof document !== 'undefined') {
  const markGesture = () => {
    hasUserGesture = true;
    applyMusicMode();
  };
  document.addEventListener('pointerdown', markGesture, { once: true, capture: true });
  document.addEventListener('keydown', markGesture, { once: true, capture: true });
  document.addEventListener('visibilitychange', () => {
    pageHidden = document.hidden;
    applyMusicMode();
  });
}

const cache: Partial<Record<SoundName, HTMLAudioElement>> = {};
const musicCache: Partial<Record<Exclude<MusicMode, null>, HTMLAudioElement>> = {};

function audio(name: SoundName): HTMLAudioElement {
  let a = cache[name];
  if (!a) {
    a = new Audio(`/sfx/${name}.wav`);
    a.preload = 'auto';
    a.volume = 0.5;
    cache[name] = a;
  }
  return a;
}

export function playSound(name: SoundName): void {
  if (!soundEnabled) return;
  try {
    const a = audio(name);
    a.currentTime = 0;
    void a.play().catch(() => {});
  } catch {
    /* ignore */
  }
}

function music(name: Exclude<MusicMode, null>): HTMLAudioElement {
  let a = musicCache[name];
  if (!a) {
    a = new Audio(`/music/${name}.mp3`);
    a.preload = 'auto';
    a.loop = true;
    a.volume = name === 'duel' ? 0.22 : 0.16;
    musicCache[name] = a;
  }
  return a;
}

function pauseMusic(): void {
  for (const a of Object.values(musicCache)) {
    if (a) a.pause();
  }
}

function applyMusicMode(): void {
  // Music is gated by its OWN toggle, independent of sound effects, and never
  // plays while the tab is hidden.
  if (!musicEnabled || !musicMode || pageHidden) {
    pauseMusic();
    return;
  }
  if (!hasUserGesture) return;
  for (const [name, a] of Object.entries(musicCache)) {
    if (a && name !== musicMode) a.pause();
  }
  try {
    void music(musicMode).play().catch(() => {});
  } catch {
    /* ignore */
  }
}

export function setMusicMode(mode: MusicMode): void {
  musicMode = mode;
  applyMusicMode();
}

function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  // iPhone/iPod, plus iPadOS (reports as "Macintosh" but is touch-capable).
  return /iP(hone|od|ad)/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

// iOS/macOS Safari implement NO Vibration API. As a best-effort, a labeled
// "switch" checkbox emits a subtle haptic tick when toggled on iOS 17.4+. It is
// most reliable during a real user gesture; purely event-driven buzzes (e.g. a
// hit landing on you) may still be silent — Safari simply has no equivalent.
let hapticSwitch: HTMLInputElement | null = null;
function iosHapticTick(): void {
  if (typeof document === 'undefined' || !document.body) return;
  if (!hapticSwitch) {
    const label = document.createElement('label');
    label.setAttribute('aria-hidden', 'true');
    label.style.cssText =
      'position:fixed;top:0;left:0;width:0;height:0;opacity:0;pointer-events:none;overflow:hidden;';
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.setAttribute('switch', ''); // the attribute that unlocks the haptic
    input.tabIndex = -1;
    label.appendChild(input);
    document.body.appendChild(label);
    hapticSwitch = input;
  }
  hapticSwitch.click(); // toggling the switch is what fires the tick
}

export function vibrate(pattern: number | number[]): void {
  if (!hapticsEnabled) return;
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      if (!hasUserGesture) return; // avoid the browser's console Intervention
      navigator.vibrate(pattern);
      return;
    }
    if (isIOS()) iosHapticTick();
  } catch {
    /* ignore */
  }
}

export const isSoundEnabled = () => soundEnabled;
export const isMusicEnabled = () => musicEnabled;
export const isHapticsEnabled = () => hapticsEnabled;

export function setSoundEnabled(v: boolean): void {
  soundEnabled = v;
  localStorage.setItem(SOUND_KEY, v ? 'on' : 'off');
  if (v) playSound('tap'); // sample so the toggle is audible
}

export function setMusicEnabled(v: boolean): void {
  musicEnabled = v;
  localStorage.setItem(MUSIC_KEY, v ? 'on' : 'off');
  applyMusicMode(); // start/stop the loop immediately
}

export function setHapticsEnabled(v: boolean): void {
  hapticsEnabled = v;
  localStorage.setItem(HAPTICS_KEY, v ? 'on' : 'off');
  if (v) vibrate(25);
}
